import type { OrganizationMemberDTO } from "./orgaAdmin.types";
import BaseApiClient from "../api/baseApiClient.service";
import type { InvitationPayload } from "../invitation/invitation.types";
import type { OrgaMemberRole } from "../auth/auth.types";

/**
 * Service for the organization admin
 * This service is used to invite users to an organization
 */
export class OrgaAdminService {
	private static instance: OrgaAdminService;
	private baseApi: BaseApiClient;

	private baseApiSubPath = "/orga-admin/users";

	private constructor() {
		this.baseApi = BaseApiClient.getInstance();
	}

	public static getInstance(): OrgaAdminService {
		if (!OrgaAdminService.instance) {
			OrgaAdminService.instance = new OrgaAdminService();
			OrgaAdminService.instance.baseApi = BaseApiClient.getInstance();
		}
		return OrgaAdminService.instance;
	}

	/**
	 * User Management
	 */

	/**
	 * Fetches the users for the given organization id
	 * @param organizationId The id of the organization to fetch the users for
	 * Will not returnusers that have the PLATFORM_ADMIN role
	 * @returns Promise with the users
	 */
	public async getUsersForOrganization(
		organizationId: string,
	): Promise<OrganizationMemberDTO[]> {
		return this.baseApi.get<OrganizationMemberDTO[]>(
			`${this.baseApiSubPath}/${organizationId}`,
		);
	}

	/**
	 * Invite a user using their e-mail to the organization
	 * @param organizationId The id of the organization to invite the user to
	 * @param email The email of the user to invite
	 * @returns Promise with the void
	 */
	public inviteUserToOrganization(
		organizationId: string,
		email: string,
	): Promise<void> {
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/invite`,
			{
				email,
			},
		);
	}

	/**
	 * Invites a bulk of users to an organization
	 * @param organizationId The id of the organization to invite the users to
	 * @param invitations The invitations to invite
	 * @returns Promise with the void
	 */
	public bulkInviteUsersToOrganization(
		organizationId: string,
		invitations: InvitationPayload[],
	): Promise<void> {
		return this.baseApi.post(
			`${this.baseApiSubPath}/${organizationId}/invite/bulk`,
			invitations,
		);
	}

	/**
	 * Cancels the invitation for the given organization id and invitation id
	 * @param organizationId The id of the organization to cancel the invitation for
	 * @param invitationId The id of the invitation to cancel
	 * TODO: Disable user instead of cancelling the invitation
	 * @returns Promise with the void
	 */
	public cancelInvitation(
		organizationId: string,
		invitationId: string,
	): Promise<void> {
		return this.baseApi.delete(
			`${this.baseApiSubPath}/${organizationId}/invitations/${invitationId}`,
		);
	}

	/**
	 * Fetches the users in the organization
	 * @param organizationId The id of the organization to fetch the users for
	 * @returns Promise with the users
	 */
	public getUsersInOrganization(
		organizationId: string,
	): Promise<OrganizationMemberDTO[]> {
		return this.baseApi.get<OrganizationMemberDTO[]>(
			`${this.baseApiSubPath}/${organizationId}`,
		);
	}

	/**
	 * Updates the organization roles for a specific user
	 * @param organizationId The id of the organization
	 * @param userId The id of the user whose roles should be updated
	 * @param orgaRoles The new organization roles to assign to the user
	 * @returns Promise with the response containing message and updated roles
	 */
	public updateUserRoles(
		organizationId: string,
		userId: string,
		orgaRoles: OrgaMemberRole[],
	): Promise<{ message: string; orgaRoles: OrgaMemberRole[] }> {
		return this.baseApi.put<
			{ message: string; orgaRoles: OrgaMemberRole[] },
			{ message: string; orgaRoles: OrgaMemberRole[] }
		>(`${this.baseApiSubPath}/${organizationId}/users/${userId}/roles`, {
			message: "Roles updated successfully",
			orgaRoles,
		});
	}
}
